// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useState, useCallback } from 'react'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  variant: ToastVariant
  duration?: number
}

const toastListeners: Array<(toast: Toast) => void> = []

export function emitToast(toast: Omit<Toast, 'id'>): void {
  const t: Toast = { ...toast, id: Math.random().toString(36).slice(2) }
  toastListeners.forEach((fn) => fn(t))
}

export const toast = {
  success: (message: string, duration = 4000) => emitToast({ message, variant: 'success', duration }),
  error: (message: string, duration = 6000) => emitToast({ message, variant: 'error', duration }),
  info: (message: string, duration = 4000) => emitToast({ message, variant: 'info', duration }),
  warning: (message: string, duration = 5000) => emitToast({ message, variant: 'warning', duration }),
}

export function useToastState() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addListener = useCallback(() => {
    const handler = (t: Toast) => {
      setToasts((prev) => [...prev, t])
      if (t.duration !== 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.id !== t.id))
        }, t.duration ?? 4000)
      }
    }
    toastListeners.push(handler)
    return () => {
      const idx = toastListeners.indexOf(handler)
      if (idx !== -1) toastListeners.splice(idx, 1)
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return { toasts, addListener, dismiss }
}
